import React, { ReactElement, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { TITLE } from "shared/util/constants/title";
import MigrationEligibilityContainer from "external/programs/migration-acceleration-program/2024/fund-request/components/create/eligibility/MigrationEligibilityContainer";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Button from "@amzn/awsui-components-react/polaris/button";
import {
  ACCURATE_WORKLOAD_ACKNOWLEDGEMENT_KEY,
  ANNUAL_RUN_RATE_KEY,
  MIGRATION_PHASE_KEY,
  MODERNIZATION_ACKNOWLEDGEMENT_KEY,
  MODERNIZATION_ANNUAL_RUN_RATE_KEY,
  VMWARE_ACKNOWLEDGEMENT_KEY,
  VMWARE_MSP_ACKNOWLEDGEMENT_KEY,
} from "external/components/FundRequest/ProgramSelectMAP2024/util";
import {
  ErrorDisplayType,
  IErrorDisplay,
  ValidationResult,
} from "external/util/common/validationHelper";
import {
  isAccurateWorkloadAcknowledgedField,
  isAccurateWorkloadAcknowledgedForm,
  isValidAnnualRunRate,
  isValidModernizationAnnualRunRate,
} from "external/programs/migration-acceleration-program/2024/fund-request/views/create/validations";
import {
  CreateMAPFundRequestRequestBuilder,
  ICreateMAPFundRequestRequest,
} from "external/util/services/data/model/createFundRequest";
import { useHistory } from "react-router-dom";
import {
  ERROR_TYPE,
  hasError,
  retryApiCall,
} from "shared/util/services/data/DataService";
import { createFundRequest } from "external/util/services/data/FundRequestService";
import { IGenericObject } from "shared/programs/migration-acceleration-program/2024/fund-request/types/CommonTypes";
import { HelpPanelContentContext } from "shared/util/context/help/HelpContext";
import { IMigrationEligibilityDisabledFields } from "external/programs/migration-acceleration-program/2024/fund-request/components/edit/eligibility/EditMigrationEligibilityContainer"; //TODO: Clean up and organization....
import { GENERIC_ERROR_MESSAGE } from "external/programs/migration-acceleration-program/2024/fund-request/components/create/wizard/Steps/Artifacts/util";

export interface IMigrationEligibilityStateModel {
  [MIGRATION_PHASE_KEY]?: string;
  [ANNUAL_RUN_RATE_KEY]?: number;
  [ACCURATE_WORKLOAD_ACKNOWLEDGEMENT_KEY]?: boolean;
  [MODERNIZATION_ACKNOWLEDGEMENT_KEY]?: boolean;
  [MODERNIZATION_ANNUAL_RUN_RATE_KEY]?: number;
  [VMWARE_ACKNOWLEDGEMENT_KEY]?: boolean;
  [VMWARE_MSP_ACKNOWLEDGEMENT_KEY]?: boolean;
}

export interface IMigrationEligibilityProps {
  disabled?: IMigrationEligibilityDisabledFields;
  formData: IMigrationEligibilityStateModel;
  fieldErrors: IMigrationEligibilityStateModel;
  formError: string;
  loading?: boolean;
  updateField: (props: IGenericObject) => void;
  updateFieldErrors: (props: IGenericObject) => void;
  updateFormError: (props: string) => void;
  setFormData: React.Dispatch<
    React.SetStateAction<IMigrationEligibilityStateModel>
  >;
  setFieldErrors: React.Dispatch<
    React.SetStateAction<IMigrationEligibilityStateModel>
  >;
  setFormError: React.Dispatch<React.SetStateAction<string>>;
  actions: ReactElement;
}

const FundRequestCreationView = () => {
  const INITIAL_STATE = {
    [MIGRATION_PHASE_KEY]: "",
  };

  const history = useHistory();
  const { setKey } = React.useContext(HelpPanelContentContext);

  const [formData, setFormData] =
    useState<IMigrationEligibilityStateModel>(INITIAL_STATE);
  const [fieldErrors, setFieldErrors] =
    useState<IMigrationEligibilityStateModel>(INITIAL_STATE);
  const [formError, setFormError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const updateField = ({ key, value }: { [key: string]: string }) => {
    setFieldErrors(INITIAL_STATE);
    setFormError("");
    setFormData((prevState) => ({ ...prevState, [key]: value }));
  };

  const updateFieldErrors = ({ key, value }: { [key: string]: string }) => {
    setFieldErrors((prevState) => ({ ...prevState, [key]: value }));
  };

  const updateFormError = (value: string) => {
    setFormError(value);
  };

  const createProps = {
    formData,
    fieldErrors,
    formError,
    isLoading,
    updateField,
    updateFieldErrors,
    updateFormError,
    setFormData,
    setFieldErrors,
    setFormError,
    setIsLoading,
  };

  useEffect(() => {
    document.title = DOMPurify.sanitize(`${TITLE} - New Fund Request`);
    setKey("eligibility.page");
  }, []);

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const validationResults: ValidationResult<string>[] = validate(formData);

      const hasErrors = handleValidationErrors({
        validationResults,
        setFieldErrors,
        setFormError,
      });

      if (hasErrors) throw new Error("Error while validating user input.");

      const payload: ICreateMAPFundRequestRequest | undefined =
        createPayload(formData);
      if (!payload) throw new Error("Error while creating 'create' payload");

      const fundRequestId = await submitFundReqest({ payload });

      if (fundRequestId) {
        console.info(`Fund request ${fundRequestId} successfully created.`);
        history.push(`/fund-requests/${fundRequestId}/new`);
      }
    } catch (error: unknown) {
      setIsLoading(false);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const validate = (
    eligibility: IMigrationEligibilityStateModel
  ): ValidationResult<string>[] => {
    const {
      migrationPhase,
      annualRunRate,
      modernizationArrUsdAmount,
      modernizationIncentiveEligible,
      accurateWorkloadAcknowledgement,
    } = eligibility;

    const validations = [
      isValidAnnualRunRate,
      isValidModernizationAnnualRunRate,
      isAccurateWorkloadAcknowledgedForm,
      isAccurateWorkloadAcknowledgedField,
    ];

    return validations.map((validate) =>
      validate({
        migrationPhase,
        annualRunRate,
        modernizationArrUsdAmount,
        modernizationIncentiveEligible,
        accurateWorkloadAcknowledgement,
      })
    );
  };

  const handleValidationErrors = ({
    validationResults,
    setFieldErrors,
    setFormError,
  }: {
    validationResults: ValidationResult<string>[];
    setFieldErrors: React.Dispatch<
      React.SetStateAction<IMigrationEligibilityStateModel>
    >;
    setFormError: React.Dispatch<React.SetStateAction<string>>;
  }) => {
    let hasErrors: boolean = false;
    if (!Array.isArray(validationResults) || validationResults.length === 0)
      return;

    const validationErrors = validationResults.filter(
      (result: ValidationResult<string>) => !result.isValid
    );
    const fieldErrors: { [key: string]: string } = {};
    let formError = "";

    validationErrors.map(({ error }: ValidationResult<string>) => {
      if (!error) return;
      const { type, fieldKey, reason }: IErrorDisplay<string> = error;
      if (type === ErrorDisplayType.FORM) {
        formError = reason;
      }

      if (type === ErrorDisplayType.FIELD && fieldKey) {
        fieldErrors[fieldKey] = reason;
      }
    });

    if (Object.keys(fieldErrors).length > 0) {
      hasErrors = true;
      setFieldErrors(fieldErrors);
    }

    if (formError) {
      hasErrors = true;
      setFormError(formError);
    }

    return hasErrors;
  };

  const createPayload = (
    state: IMigrationEligibilityStateModel
  ): ICreateMAPFundRequestRequest | undefined => {
    const {
      migrationPhase,
      annualRunRate,
      accurateWorkloadAcknowledgement,
      modernizationIncentiveEligible,
      modernizationArrUsdAmount,
      vmwareAcknowledgement,
      vmwareMSPAcknowledgement,
    }: IMigrationEligibilityStateModel = state;

    if (!migrationPhase || !annualRunRate) return;

    const payload = new CreateMAPFundRequestRequestBuilder(
      migrationPhase,
      annualRunRate
    )
      .setAccurateWorkloadAcknowledgement(accurateWorkloadAcknowledgement)
      .setModernizationIncentiveEligible(modernizationIncentiveEligible)
      .setModernizationArrUsdAmount(modernizationArrUsdAmount)
      .setVmwareAcknowledgement(vmwareAcknowledgement)
      .setVmwareMSPAcknowledgement(vmwareMSPAcknowledgement)
      .build();

    return payload.toJson();
  };

  const submitFundReqest = async ({
    payload,
  }: {
    payload: ICreateMAPFundRequestRequest;
  }): Promise<string | undefined> => {
    // @ts-expect-error Still in javascript, so the implementation details don't matter
    const response = await retryApiCall({
      callApi: createFundRequest(payload),
    });
    if (hasError(response)) {
      handleApiError(response.errorType)({
        response,
        setFieldErrors,
        setFormError,
      });
      return;
    }

    return response.fundRequestId;
  };

  const handleApiError = (errorType: string) => {
    const errorHandlers = {
      [ERROR_TYPE.FIELD]: handleFieldError,
      [ERROR_TYPE.BANNER]: handleFormError,
    };

    return errorHandlers[errorType];
  };

  const handleFieldError = ({
    response,
    setFieldErrors,
  }: {
    response: IGenericObject;
    setFieldErrors: React.Dispatch<
      React.SetStateAction<IMigrationEligibilityStateModel>
    >;
  }) => {
    setFieldErrors(response.errors);
  };

  const handleFormError = ({
    response,
    setFormError,
  }: {
    response: IGenericObject;
    setFormError: React.Dispatch<React.SetStateAction<string>>;
  }) => {
    setFormError(response?.message ?? GENERIC_ERROR_MESSAGE);
  };

  return (
    <MigrationEligibilityContainer
      {...createProps}
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            loading={isLoading}
            variant="link"
            onClick={() => {
              history.push("/dashboard");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isLoading}
            onClick={async () => {
              await onSubmit();
            }}
          >
            Start fund request
          </Button>
        </SpaceBetween>
      }
    />
  );
};

export default FundRequestCreationView;
